import React, { useId } from 'react';

import cn from '~/Utils/cn';

import { CONVERSATION_TEXTS } from '../../conversation/texts';

/** Where each dot starts into its pulse, so the three run as a wave. Full literal classes, so Tailwind's content scan keeps them. */
const DOT_DELAYS = ['[animation-delay:0ms]', '[animation-delay:200ms]', '[animation-delay:400ms]'];

interface WorkingCursorProps {
  isWorking: boolean;
}

/**
 * Clive's cursor at the newest end of the log while a run works on the claim: Clive's dots, in the purple its reply
 * wears, named for a screen reader. The status says a run has started before the run's own entries do. Spaced as an
 * entry is, and level with what the entries say rather than with their rail; still for a reader who asked for less
 * motion. The name is spoken from hidden text inside the region, as a live region announces its text, not its label.
 */
const WorkingCursor: React.FC<WorkingCursorProps> = ({ isWorking }) => {
  const labelId = useId();

  // Mounted while idle, so a screen reader announces the run as it starts; boxed and named only while it works.
  // Positioned, idle too, so the hidden name is placed in it: otherwise its box is the page's, and at the foot of a
  // long log the name stretches the page as tall as the log.
  return (
    <div
      role="status"
      aria-labelledby={isWorking ? labelId : undefined}
      className={cn('relative', isWorking && 'mb-16 flex gap-12 last:mb-0')}
    >
      {isWorking ? (
        <>
          <span id={labelId} className="sr-only">
            {CONVERSATION_TEXTS.WORKING}
          </span>
          <div className="w-20 shrink-0" aria-hidden />
          {/* A line of small text tall: the dots keep their place under the entry above, and clear of the foot. */}
          <div className="flex h-[18px] items-center">
            <span className="flex gap-4" aria-hidden>
              {DOT_DELAYS.map((delay) => (
                <span
                  key={delay}
                  className={cn(
                    'h-[5px] w-[5px] animate-pulse rounded-full bg-purple-600 motion-reduce:animate-none',
                    delay
                  )}
                />
              ))}
            </span>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default WorkingCursor;
